import {
  useGetProviderPortalMetrics,
  useListSitesByProviderUser,
  useListStatus,
} from 'api/resource';
import { useCombineStates } from 'hooks/useCombineStates';
import { getCutoffRange, nowISO, parseDateTime } from 'utils/dateUtils';
import { normalizeProviderDashboardPreferences } from 'store/SettingsStore';
import { useCurrentUserStore } from 'store/UserStore';
import { useSettingsStore } from 'store/SettingsStore';
import { useCurrentSiteStore } from 'store/SiteStore';
import { usePaymentCycleRefresh } from './usePaymentCycleRefresh';

const sumAmounts = (...values: Array<number | null | undefined>) =>
  values.reduce((total, value) => total + Number(value || 0), 0);

const getSummaryLastActivity = (currentSummaryPeriod?: {
  public?: { 'last-submission-upload-date'?: string | null };
  'third-party'?: { 'last-submission-upload-date'?: string | null };
  direct?: { 'last-submission-upload-date'?: string | null };
}) =>
  currentSummaryPeriod?.public?.['last-submission-upload-date'] ??
  currentSummaryPeriod?.['third-party']?.['last-submission-upload-date'] ??
  currentSummaryPeriod?.direct?.['last-submission-upload-date'] ??
  null;

export const useProviderDashboardData = (selectedUserID?: string) => {
  const { id: currentUserID } = useCurrentUserStore();
  const {
    id: currentSiteID,
    providerSiteSelection,
    timezone,
    province,
  } = useCurrentSiteStore();
  const userID = selectedUserID ?? currentUserID;
  const {
    fiscalYearEnd,
    providerDashboardDateBasis,
    providerDashboardSummaryMode,
  } = useSettingsStore();
  const normalizedProviderDashboardPreferences =
    normalizeProviderDashboardPreferences({
      summaryMode: providerDashboardSummaryMode,
      dateBasis: providerDashboardDateBasis,
    });
  const effectiveProviderDashboardSummaryMode =
    normalizedProviderDashboardPreferences.providerDashboardSummaryMode;
  const effectiveProviderDashboardDateBasis =
    normalizedProviderDashboardPreferences.providerDashboardDateBasis;
  const today = parseDateTime(nowISO(), { zone: timezone });
  const payPeriodRange =
    effectiveProviderDashboardSummaryMode === 'pay-period'
      ? getCutoffRange(today, undefined, province)
      : null;
  const currentSummaryExpectedPaymentDate =
    effectiveProviderDashboardSummaryMode === 'pay-period'
      ? today.day >= 15
        ? today.endOf('month').toISODate()
        : today.set({ day: 15 }).toISODate()
      : null;
  const currentSummaryNextCutoffDate =
    effectiveProviderDashboardSummaryMode === 'pay-period'
      ? payPeriodRange?.end.toISODate() ?? null
      : null;

  const sites = useListSitesByProviderUser({ userID });
  const siteID =
    !selectedUserID && providerSiteSelection === 'all'
      ? Array.from(new Set((sites.data ?? []).map((site) => site.id))).sort()
      : currentSiteID;
  const metrics = useGetProviderPortalMetrics({
    userID,
    siteID,
    today: today.toISO(),
    fiscalYearEnd: fiscalYearEnd ?? 12,
    dateBasis: effectiveProviderDashboardDateBasis,
    summaryMode: effectiveProviderDashboardSummaryMode,
  });
  const statuses = useListStatus({});
  const paymentCycleEnabled =
    effectiveProviderDashboardSummaryMode === 'pay-period' &&
    (Boolean(selectedUserID) || providerSiteSelection !== 'all');
  usePaymentCycleRefresh(
    paymentCycleEnabled && Boolean(userID && currentSiteID && metrics.refetch),
    timezone,
    metrics.refetch
  );
  const { isLoading, isError } = useCombineStates([metrics, sites, statuses]);

  const currentSummaryPeriod = metrics.data?.['current-summary-period'];
  const lastSummaryPeriod = metrics.data?.['last-summary-period'];

  return {
    paymentCycle: paymentCycleEnabled
      ? metrics.data?.['payment-cycle'] ?? null
      : null,
    isLoading,
    isError,
    dateBasis: effectiveProviderDashboardDateBasis,
    summaryMode: effectiveProviderDashboardSummaryMode,
    allTimeOutstanding: metrics.data?.['all-time-outstanding'],
    ytdOutstandingAmount: metrics.data?.ytd?.public?.outstanding,
    ytdPrivateOutstandingAmount:
      metrics.data?.ytd?.['third-party']?.outstanding,
    ytdDirectOutstandingAmount: metrics.data?.ytd?.direct?.outstanding,
    ytdPaidAmount: metrics.data?.ytd?.public?.paid,
    ytdSubmittedAmount: metrics.data?.ytd?.public?.submitted,
    ytdWrittenOffAmount: metrics.data?.ytd?.public?.['written-off'],
    ytdStartDate: metrics.data?.['ytd-period']?.['start-date'],
    ytdEndDate: metrics.data?.['ytd-period']?.['end-date'],
    ytdPrivatePaidAmount: metrics.data?.ytd?.['third-party']?.paid,
    ytdPrivateSubmittedAmount: metrics.data?.ytd?.['third-party']?.submitted,
    ytdPrivateWrittenOffAmount:
      metrics.data?.ytd?.['third-party']?.['written-off'],
    ytdDirectPaidAmount: metrics.data?.ytd?.direct?.paid,
    ytdDirectSubmittedAmount: metrics.data?.ytd?.direct?.submitted,
    ytdDirectWrittenOffAmount: metrics.data?.ytd?.direct?.['written-off'],
    currentSummaryOutstandingAmount: sumAmounts(
      currentSummaryPeriod?.public?.outstanding,
      currentSummaryPeriod?.['third-party']?.outstanding,
      currentSummaryPeriod?.direct?.outstanding
    ),
    currentSummaryPaidAmount: sumAmounts(
      currentSummaryPeriod?.public?.paid,
      currentSummaryPeriod?.['third-party']?.paid,
      currentSummaryPeriod?.direct?.paid
    ),
    currentSummarySubmittedAmount: sumAmounts(
      currentSummaryPeriod?.public?.submitted,
      currentSummaryPeriod?.['third-party']?.submitted,
      currentSummaryPeriod?.direct?.submitted
    ),
    currentSummaryWrittenOffAmount: 0,
    currentSummaryLastActivity: getSummaryLastActivity(currentSummaryPeriod),
    currentSummaryPeriodStart: currentSummaryPeriod?.start,
    currentSummaryPeriodEnd: currentSummaryPeriod?.end,
    currentSummaryExpectedPaymentDate,
    currentSummaryNextCutoffDate,
    lastSummaryOutstandingAmount: sumAmounts(
      lastSummaryPeriod?.public?.outstanding,
      lastSummaryPeriod?.['third-party']?.outstanding,
      lastSummaryPeriod?.direct?.outstanding
    ),
    lastSummaryPaidAmount: sumAmounts(
      lastSummaryPeriod?.public?.paid,
      lastSummaryPeriod?.['third-party']?.paid,
      lastSummaryPeriod?.direct?.paid
    ),
    lastSummarySubmittedAmount: sumAmounts(
      lastSummaryPeriod?.public?.submitted,
      lastSummaryPeriod?.['third-party']?.submitted,
      lastSummaryPeriod?.direct?.submitted
    ),
    lastSummaryWrittenOffAmount: sumAmounts(
      lastSummaryPeriod?.public?.['written-off'],
      lastSummaryPeriod?.['third-party']?.['written-off'],
      lastSummaryPeriod?.direct?.['written-off']
    ),
    lastSummaryPaymentDate:
      lastSummaryPeriod?.public?.['payment-date'] ??
      lastSummaryPeriod?.['third-party']?.['payment-date'] ??
      lastSummaryPeriod?.direct?.['payment-date'] ??
      null,
    lastSummaryPeriodStart: lastSummaryPeriod?.start,
    lastSummaryPeriodEnd: lastSummaryPeriod?.end,
  };
};
